import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { evaluateFilterRules } from '@/utils/adjustmentFilterUtils';
import { AdjustmentSettings, FilterRule } from '@/types/adjustment-filters';
import { computeAdjustedEstimateTotal, BaseCostEntry } from '@/utils/estimateTotalCalculation';

export interface Customer {
  id: string;
  name: string;
  last_name: string;
  customer_type: string;
  business_name?: string | null;
  representative_first_name?: string | null;
  representative_last_name?: string | null;
  cell_numbers: string[];
  emails: string[];
  city?: string;
  province_state?: string;
  suburb?: string;
  postal_code?: string;
  street_address?: string;
  lead_source?: string;
  lead_source_description?: string;
  lead_owner_id?: string;
  referred_by_id?: string;
  tags?: string[];
  created_at: string;
}

export interface Project {
  id: string;
  name: string;
  status?: string;
}

export interface Profile {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
}

export interface Estimate {
  id: string;
  estimate_number?: string | null;
  title: string;
  description?: string;
  status: string;
  version: number;
  tax_percentage?: number;
  tax_inclusive?: boolean;
  payment_terms?: string;
  special_instructions?: string;
  validity_period?: number;
  expires_at?: string;
  created_at: string;
  updated_at: string;
  sent_date?: string;
  accepted_date?: string;
  rejected_date?: string;
  expired_date?: string;
  total_invoiced?: number;
  invoicing_status?: string;
  customer_id?: string;
  project_id?: string;
  created_by: string;
  customers?: Customer | null;
  projects?: Project | null;
  profiles?: Profile | null;
  // Calculated fields
  total_amount?: number;
  invoice_count?: number;
  invoice_paid_count?: number;
  invoice_sent_count?: number;
  invoice_cancelled_count?: number;
  has_invoices?: boolean;
  has_project?: boolean;
  has_versions?: boolean;
  version_count?: number;
  lead_age_days?: number;
}

export const useEstimateData = () => {
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchEstimates = useCallback(async () => {
    try {
      // Fetch estimates with comprehensive joins
      const { data: estimatesData, error } = await supabase
        .from('estimates')
        .select(`
          *,
          customers (
            id,
            name,
            last_name,
            customer_type,
            business_name,
            representative_first_name,
            representative_last_name,
            cell_numbers,
            emails,
            city,
            province_state,
            suburb,
            postal_code,
            street_address,
            lead_source,
            lead_source_description,
            lead_owner_id,
            referred_by_id,
            tags,
            created_at
          ),
          projects!estimates_project_id_fkey (
            id,
            name,
            status
          )
        `)
        .order(sortBy, { ascending: sortDir === 'asc' });

      if (error) {
        console.error('Error fetching estimates:', error);
        return;
      }

      const estimatesWithCalculations: Estimate[] = [];

      // Early exit if no estimates
      if (!estimatesData || estimatesData.length === 0) {
        setEstimates([]);
        return;
      }

      // Collect estimate IDs for batched queries
      const estimateIds = estimatesData.map((e: any) => e.id);

      // Fetch related data in parallel (batch queries)
      const [lineItemsRes, invoicesRes, versionsRes] = await Promise.all([
        supabase
          .from('estimate_line_items')
          .select('estimate_id, total_cost_snapshot, manual_override, manual_total_cost, component_type, component_id, category_id, quantity_in_base_units')
          .in('estimate_id', estimateIds),
        supabase
          .from('invoices')
          .select('estimate_id, status')
          .in('estimate_id', estimateIds),
        supabase
          .from('estimate_versions')
          .select('estimate_id, id')
          .in('estimate_id', estimateIds)
      ]);

      if (lineItemsRes.error) {
        console.error('Error fetching line items (batch):', lineItemsRes.error);
      }
      if (invoicesRes.error) {
        console.error('Error fetching invoices (batch):', invoicesRes.error);
      }
      if (versionsRes.error) {
        console.error('Error fetching versions (batch):', versionsRes.error);
      }

      const lineItemsData = lineItemsRes.data || [];
      const invoicesData = invoicesRes.data || [];
      const versionsData = versionsRes.data || [];

      // Group results by estimate_id
      const lineItemsByEstimate = new Map<string, any[]>();
      for (const item of lineItemsData) {
        const key = item.estimate_id as string;
        if (!lineItemsByEstimate.has(key)) lineItemsByEstimate.set(key, []);
        lineItemsByEstimate.get(key)!.push(item);
      }

      const invoicesByEstimate = new Map<string, any[]>();
      for (const inv of invoicesData) {
        const key = inv.estimate_id as string;
        if (!invoicesByEstimate.has(key)) invoicesByEstimate.set(key, []);
        invoicesByEstimate.get(key)!.push(inv);
      }

      const versionsByEstimate = new Map<string, any[]>();
      for (const ver of versionsData) {
        const key = ver.estimate_id as string;
        if (!versionsByEstimate.has(key)) versionsByEstimate.set(key, []);
        versionsByEstimate.get(key)!.push(ver);
      }

      // Batch-fetch base costs from source tables for adjustment filter evaluation
      const allSupplierIds: string[] = [];
      const allInternalIds: string[] = [];
      for (const item of lineItemsData) {
        if (item.component_type === 'supplier_line_item') allSupplierIds.push(item.component_id as string);
        else if (item.component_type === 'internal_line_item') allInternalIds.push(item.component_id as string);
      }

      const baseCostMap = new Map<string, { base_cost: number; category_id?: string; trade_id?: string }>();

      const baseCostQueries: Promise<void>[] = [];
      if (allSupplierIds.length > 0) {
        baseCostQueries.push(
          (async () => {
            const { data } = await supabase
              .from('internal_line_items')
              .select('id, base_cost, category_id')
              .in('id', allSupplierIds);
            (data || []).forEach((d: any) => baseCostMap.set(d.id, { base_cost: d.base_cost, category_id: d.category_id, trade_id: undefined }));
          })()
        );
      }
      if (allInternalIds.length > 0) {
        baseCostQueries.push(
          (async () => {
            const { data } = await supabase
              .from('internal_line_items')
              .select('id, base_cost, category_id')
              .in('id', allInternalIds);
            (data || []).forEach((d: any) => baseCostMap.set(d.id, { base_cost: d.base_cost, category_id: d.category_id }));
          })()
        );
      }
      await Promise.all(baseCostQueries);

      // Build final results using grouped data
      for (const estimate of estimatesData || []) {
        const items = lineItemsByEstimate.get(estimate.id) || [];
        const invs = invoicesByEstimate.get(estimate.id) || [];
        const vers = versionsByEstimate.get(estimate.id) || [];

        // Use centralized utility for adjusted total calculation
        const adjMeta = estimate.adjustment_metadata as any;
        
        // Build a per-estimate baseCostMap subset from the global one
        const itemBaseCostMap = new Map<string, BaseCostEntry>();
        for (const item of items) {
          const entry = baseCostMap.get(item.component_id as string);
          if (entry) itemBaseCostMap.set(item.component_id as string, entry);
        }

        const { adjustedSubtotal: subtotalAfterDiscount } = computeAdjustedEstimateTotal(
          items,
          adjMeta,
          itemBaseCostMap
        );

        // Apply VAT if applicable
        let totalAmount = subtotalAfterDiscount;
        if (estimate.tax_percentage && !estimate.tax_inclusive) {
          totalAmount = subtotalAfterDiscount + (subtotalAfterDiscount * estimate.tax_percentage / 100);
        }

        // Invoice counts
        const invoiceCount = invs.length || 0;
        const invoicePaidCount = invs.filter((inv: any) => inv.status === 'paid').length || 0;
        const invoiceSentCount = invs.filter((inv: any) => inv.status === 'sent').length || 0;
        const invoiceCancelledCount = invs.filter((inv: any) => inv.status === 'cancelled').length || 0;

        // Version count
        const versionCount = vers.length || 0;

        // Handle potential join errors
        const safeEstimate: Estimate = {
          ...estimate,
          customers: estimate.customers && 'id' in estimate.customers ? estimate.customers as Customer : null,
          projects: estimate.projects && 'id' in estimate.projects ? estimate.projects as Project : null
        };

        // Calculate lead age
        const leadAgeDays = safeEstimate.customers?.created_at
          ? Math.floor((new Date().getTime() - new Date(safeEstimate.customers.created_at).getTime()) / (1000 * 60 * 60 * 24))
          : null;

        estimatesWithCalculations.push({
          ...safeEstimate,
          total_amount: totalAmount,
          invoice_count: invoiceCount,
          invoice_paid_count: invoicePaidCount,
          invoice_sent_count: invoiceSentCount,
          invoice_cancelled_count: invoiceCancelledCount,
          has_invoices: invoiceCount > 0,
          has_project: !!estimate.project_id,
          has_versions: versionCount > 1,
          version_count: versionCount,
          lead_age_days: leadAgeDays,
        });
      }

      setEstimates(estimatesWithCalculations);
    } catch (error) {
      console.error('Error fetching estimates:', error);
    }
  }, [sortBy, sortDir]);

  // Apply search to estimates
  const searchedEstimates = useMemo(() => {
    if (!search.trim()) return estimates;

    const searchTerm = search.toLowerCase();
    return estimates.filter(estimate => {
      const customerName = estimate.customers 
        ? `${estimate.customers.name} ${estimate.customers.last_name}`.toLowerCase()
        : '';
      
      return (
        estimate.estimate_number?.toLowerCase().includes(searchTerm) ||
        estimate.title?.toLowerCase().includes(searchTerm) ||
        customerName.includes(searchTerm) ||
        estimate.status.toLowerCase().includes(searchTerm) ||
        estimate.description?.toLowerCase().includes(searchTerm)
      );
    });
  }, [estimates, search]);

  // Initial fetch and re-fetch when sort parameters change
  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  return {
    estimates: searchedEstimates,
    fetchEstimates,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  };
};